import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    SimpleGrid,
    Text,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';

import { TradePlanDetailType } from '../../../_openapi/portfolio-optimizer';
import { BooleanCell } from '../../../widgets/Table/BooleanCell';
import { dateTimeCol, moneyCol, numberCol, textCol } from '../../../widgets/Table/columnHelpers/colDef';
import { MoneyCell } from '../../../widgets/Table/MoneyCell';
import { NumberCell } from '../../../widgets/Table/NumberCell';
import { TextCell } from '../../../widgets/Table/TextCell';
import { DetailsBlock } from './DetailsBlock';
import {
    DirectStocksSellTradePlanPreferences,
    DirectStocksSellTradePlanTradeInput,
    DirectStocksSellTradePlanUniverse,
    DirectStocksTradePlanPortfolioConfig,
    TradeDetailRecord,
} from './tradeDetailsModel';

export const DirectStocksSellDetails = ({
    tradePlanDetails,
}: {
    tradePlanDetails: Partial<Record<TradePlanDetailType, TradeDetailRecord>>;
}) => {
    const portfolio = tradePlanDetails[TradePlanDetailType.PORTFOLIO_CONFIG]?.detailJson?.[0];
    const universe = tradePlanDetails[TradePlanDetailType.UNIVERSE]?.detailJson;
    const preferences = tradePlanDetails[TradePlanDetailType.PREFERENCES]?.detailJson;
    const tradeInput = tradePlanDetails[TradePlanDetailType.TRADE_INPUT]?.detailJson;

    return (
        <Accordion allowMultiple>
            {portfolio && <Portfolio portfolio={portfolio as DirectStocksTradePlanPortfolioConfig} />}
            {universe && (
                <DetailsBlock
                    data={universe as DirectStocksSellTradePlanUniverse[]}
                    columns={universeColumns}
                    columnSettingsKey="directStocksSellDetailsUniverse"
                    title="Universe"
                    initialSort={{ id: 'marketValue', desc: true }}
                    paginate
                />
            )}
            {preferences && (
                <DetailsBlock
                    data={preferences as DirectStocksSellTradePlanPreferences[]}
                    columns={preferencesColumns}
                    columnSettingsKey="directStocksSellDetailsPreferences"
                    title="Preferences"
                    initialSort={{ id: 'preferencesRank', desc: false }}
                    paginate
                />
            )}
            {tradeInput && (
                <DetailsBlock
                    data={tradeInput as DirectStocksSellTradePlanTradeInput[]}
                    columns={tradeInputsColumns}
                    columnSettingsKey="directStocksSellDetailsTradeInput"
                    title="Trade Input"
                    initialSort={{ id: 'tradeInputRank', desc: false }}
                    paginate
                />
            )}
        </Accordion>
    );
};

const Portfolio = ({ portfolio }: { portfolio: DirectStocksTradePlanPortfolioConfig }) => {
    return (
        <AccordionItem>
            <AccordionButton background="whiteAlpha.50">
                <Box as="span" flex="1" textAlign="left">
                    Portfolio
                </Box>
                <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
                <SimpleGrid columns={4} columnGap={20}>
                    <Text fontSize="sm">NAV</Text>
                    <MoneyCell size="sm" value={portfolio.nav} />
                    <Text fontSize="sm">Cash</Text>
                    <MoneyCell size="sm" value={portfolio.cash} />

                    <Text fontSize="sm">Target Growth Sleeve</Text>
                    <MoneyCell size="sm" value={portfolio.targetGrowthSleeve} />
                </SimpleGrid>

                <Text mt={4} fontWeight="medium" textDecoration="underline">
                    Portfolio Setup
                </Text>
                <SimpleGrid columns={4} columnGap={20}>
                    <Text fontSize="sm">NDVR Portfolio Type</Text>
                    <TextCell textAlign="right" size="sm" value={portfolio.ndvrPortfolioType} />
                    <Text fontSize="sm">Review Status</Text>
                    <TextCell textAlign="right" size="sm" value={portfolio.reviewStatus} />

                    <Text fontSize="sm">Suspended</Text>
                    <BooleanCell textAlign="right" size="sm" value={portfolio.isSuspended} displayFalse />
                    <Text fontSize="sm">Onboarded</Text>
                    <BooleanCell textAlign="right" size="sm" value={portfolio.isOnboarded} displayFalse />

                    <Text fontSize="sm">Mirror Linked</Text>
                    <BooleanCell textAlign="right" size="sm" value={portfolio.isMirrorLinked} displayFalse />
                </SimpleGrid>

                <Text mt={4} fontWeight="medium" textDecoration="underline">
                    Portfolio Weights & Constraints
                </Text>
                <SimpleGrid columns={4} columnGap={20}>
                    <Text fontSize="sm">Target Equity Weight US</Text>
                    <NumberCell style="percent" size="sm" value={Number(portfolio.targetEquityWeightUs) * 100} />
                    <Text fontSize="sm">Current Equity Value USA</Text>
                    <NumberCell size="sm" value={portfolio.currentEquityValueUsa} />

                    <Text fontSize="sm">Target Equity Value USA</Text>
                    <NumberCell size="sm" value={portfolio.targetEquityValueUsa} />
                    <Text fontSize="sm">Active Equity Value USA</Text>
                    <NumberCell size="sm" value={portfolio.activeEquityValueUsa} />

                    <Text fontSize="sm">Max Sell Allowance</Text>
                    <NumberCell size="sm" value={portfolio.maxSellAllowance} />
                    <Text fontSize="sm">Min Sell Allowance</Text>
                    <NumberCell size="sm" value={portfolio.minSellAllowance} />

                    <Text fontSize="sm">Max Buy Allowance</Text>
                    <NumberCell size="sm" value={portfolio.maxBuyAllowance} />
                </SimpleGrid>

                <Text mt={4} fontWeight="medium" textDecoration="underline">
                    Investment Style
                </Text>
                <SimpleGrid columns={4} columnGap={20}>
                    <Text fontSize="sm">Taxable</Text>
                    <BooleanCell textAlign="right" size="sm" value={portfolio.isTaxable} displayFalse />
                    <Text fontSize="sm">Tax Loss Harvesting</Text>
                    <BooleanCell textAlign="right" size="sm" value={portfolio.isTaxLossHarvesting} displayFalse />

                    <Text fontSize="sm">Levered</Text>
                    <BooleanCell textAlign="right" size="sm" value={portfolio.isLevered} displayFalse />

                    <Text fontSize="sm">Momentum Factor</Text>
                    <BooleanCell textAlign="right" size="sm" value={portfolio.isFactorMomentum} displayFalse />
                    <Text fontSize="sm">Value Factor</Text>
                    <BooleanCell textAlign="right" size="sm" value={portfolio.isFactorValue} displayFalse />

                    <Text fontSize="sm">Low Volatility Factor</Text>
                    <BooleanCell textAlign="right" size="sm" value={portfolio.isFactorLowVolatility} displayFalse />
                    <Text fontSize="sm">Quality Factor</Text>
                    <BooleanCell textAlign="right" size="sm" value={portfolio.isFactorQuality} displayFalse />

                    <Text fontSize="sm">Factor Combination</Text>
                    <TextCell textAlign="right" size="sm" value={portfolio.factorCombination} />
                    <Text fontSize="sm">Number of Factors</Text>
                    <NumberCell size="sm" value={portfolio.numFactors} fractionDigits={0} />
                </SimpleGrid>

                <Text mt={4} fontWeight="medium" textDecoration="underline">
                    Params for Preference Calculation
                </Text>
                <SimpleGrid columns={4} columnGap={20}>
                    <Text fontSize="sm">TLH Active Ratio</Text>
                    <NumberCell size="sm" value={portfolio.tlhActiveRatio} />
                    <Text fontSize="sm">Min Size Value</Text>
                    <MoneyCell size="sm" value={portfolio.minSizeValue} />

                    <Text fontSize="sm">Min Active Value</Text>
                    <MoneyCell size="sm" value={portfolio.minActiveValue} />
                    <Text fontSize="sm">Min Sell Value</Text>
                    <MoneyCell size="sm" value={portfolio.minSellValue} />

                    <Text fontSize="sm">Min Sell Ratio</Text>
                    <NumberCell size="sm" value={portfolio.minSellRatio} />
                    <Text fontSize="sm">Min Sell Threshold</Text>
                    <MoneyCell size="sm" value={portfolio.minSellThreshold} />

                    <Text fontSize="sm">Min Buy Value</Text>
                    <MoneyCell size="sm" value={portfolio.minBuyValue} />
                    <Text fontSize="sm">Min Buy Ratio</Text>
                    <NumberCell size="sm" value={portfolio.minBuyRatio} />

                    <Text fontSize="sm">Factor Active Value</Text>
                    <MoneyCell size="sm" value={portfolio.factorActiveValue} />
                    <Text fontSize="sm">Factor Winner Threshold</Text>
                    <TextCell textAlign="right" size="sm" value={portfolio.factorWinnerThreshold} />

                    <Text fontSize="sm">Factor Active Ratio</Text>
                    <NumberCell size="sm" value={portfolio.factorActiveRatio} />
                    <Text fontSize="sm">TLH Loss Threshold Low</Text>
                    <NumberCell size="sm" value={portfolio.tlhLossThresholdLow} />

                    <Text fontSize="sm">TLH Loss Threshold High</Text>
                    <NumberCell size="sm" value={portfolio.tlhLossThresholdHigh} />
                    <Text fontSize="sm">TLH Active Value</Text>
                    <MoneyCell size="sm" value={portfolio.tlhActiveValue} />
                </SimpleGrid>

                <Text mt={4} fontWeight="medium" textDecoration="underline">
                    Partial Rebalance
                </Text>
                <SimpleGrid columns={4} columnGap={20}>
                    <Text fontSize="sm">Active Weight Divisor</Text>
                    <NumberCell size="sm" value={portfolio.activeWeightDivisor} />
                    <Text fontSize="sm">Cash Allocation Ratio</Text>
                    <NumberCell size="sm" value={portfolio.cashAllocationRatio} />

                    <Text fontSize="sm">Max Sell Turnover Ratio</Text>
                    <NumberCell size="sm" value={portfolio.maxSellTurnoverRatio} />
                </SimpleGrid>
            </AccordionPanel>
        </AccordionItem>
    );
};

const universeColumnHelper = createColumnHelper<DirectStocksSellTradePlanUniverse>();
const universeColumns = [
    universeColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    universeColumnHelper.accessor('scId', textCol({ id: 'scId' })),
    universeColumnHelper.accessor('symbol', textCol({ id: 'symbol' })),
    universeColumnHelper.accessor('quantity', numberCol({ id: 'quantity' })),
    universeColumnHelper.accessor('marketValue', moneyCol({ id: 'marketValue' })),
    universeColumnHelper.accessor(
        'benchmarkWeight',
        numberCol({ id: 'benchmarkWeight', format: { fractionDigits: 6 } }),
    ),
    universeColumnHelper.accessor('passiveRank', numberCol({ id: 'passiveRank' })),
    universeColumnHelper.accessor('factorRank', numberCol({ id: 'factorRank' })),
    universeColumnHelper.accessor('factorCombination', textCol({ id: 'factorCombination' })),
    universeColumnHelper.accessor('tlhSharesRatioLow', numberCol({ id: 'tlhSharesRatioLow' })),
    universeColumnHelper.accessor('tlhSharesRatioHigh', numberCol({ id: 'tlhSharesRatioHigh' })),
    universeColumnHelper.accessor('unrealizedReturnLow', numberCol({ id: 'unrealizedReturnLow' })),
    universeColumnHelper.accessor('unrealizedReturnHigh', numberCol({ id: 'unrealizedReturnHigh' })),
];

const preferencesColumnHelper = createColumnHelper<DirectStocksSellTradePlanPreferences>();
const preferencesColumns = [
    preferencesColumnHelper.accessor(
        'preferencesRank',
        numberCol({ id: 'preferencesRank', format: { fractionDigits: 0 } }),
    ),
    preferencesColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    preferencesColumnHelper.accessor('scId', textCol({ id: 'scId' })),
    preferencesColumnHelper.accessor('symbol', textCol({ id: 'symbol' })),
    preferencesColumnHelper.accessor('quantity', numberCol({ id: 'quantity' })),
    preferencesColumnHelper.accessor('marketValue', moneyCol({ id: 'marketValue' })),
    preferencesColumnHelper.accessor('activeRatio', numberCol({ id: 'activeRatio' })),
    preferencesColumnHelper.accessor('reason', textCol({ id: 'reason' })),
    preferencesColumnHelper.accessor(
        'benchmarkWeight',
        numberCol({ id: 'benchmarkWeight', format: { fractionDigits: 6 } }),
    ),
    preferencesColumnHelper.accessor('passiveRank', numberCol({ id: 'passiveRank' })),
    preferencesColumnHelper.accessor('factorRank', numberCol({ id: 'factorRank' })),
    preferencesColumnHelper.accessor('unrealizedReturnLow', numberCol({ id: 'unrealizedReturnLow' })),
    preferencesColumnHelper.accessor('unrealizedReturnHigh', numberCol({ id: 'unrealizedReturnHigh' })),
    preferencesColumnHelper.accessor('tlhSharesRatioLow', numberCol({ id: 'tlhSharesRatioLow' })),
    preferencesColumnHelper.accessor('tlhSharesRatioHigh', numberCol({ id: 'tlhSharesRatioHigh' })),
    preferencesColumnHelper.accessor('universeCategory', textCol({ id: 'universeCategory' })),
    preferencesColumnHelper.accessor('targetEquityValueUsa', moneyCol({ id: 'targetEquityValueUsa' })),
    preferencesColumnHelper.accessor('numFactors', numberCol({ id: 'numFactors' })),
    preferencesColumnHelper.accessor('factorWinnerThreshold', numberCol({ id: 'factorWinnerThreshold' })),
    preferencesColumnHelper.accessor('factorActiveValue', moneyCol({ id: 'factorActiveValue' })),
    preferencesColumnHelper.accessor('tlhActiveValue', moneyCol({ id: 'tlhActiveValue' })),
    preferencesColumnHelper.accessor('minSellThreshold', moneyCol({ id: 'minSellThreshold' })),
    preferencesColumnHelper.accessor('minSellValue', moneyCol({ id: 'minSellValue' })),
    preferencesColumnHelper.accessor('minBuyValue', moneyCol({ id: 'minBuyValue' })),
    preferencesColumnHelper.accessor('tlhLossThresholdLow', moneyCol({ id: 'tlhLossThresholdLow' })),
    preferencesColumnHelper.accessor('tlhLossThresholdHigh', moneyCol({ id: 'tlhLossThresholdHigh' })),
    preferencesColumnHelper.accessor('targetWeight', moneyCol({ id: 'targetWeight', format: { fractionDigits: 6 } })),
    preferencesColumnHelper.accessor('targetBenchmarkValue', moneyCol({ id: 'targetBenchmarkValue' })),
    preferencesColumnHelper.accessor('stockPreferenceCategory', textCol({ id: 'stockPreferenceCategory' })),
    preferencesColumnHelper.accessor('activeSellValue', moneyCol({ id: 'activeSellValue' })),
    preferencesColumnHelper.accessor('overweightValue', moneyCol({ id: 'overweightValue' })),
    preferencesColumnHelper.accessor('tlhValueHigh', moneyCol({ id: 'tlhValueHigh' })),
    preferencesColumnHelper.accessor('factorLoserValue', moneyCol({ id: 'factorLoserValue' })),
    preferencesColumnHelper.accessor('sellUniverseLen', numberCol({ id: 'sellUniverseLen' })),
    preferencesColumnHelper.accessor('outOfRankThreshold', moneyCol({ id: 'outOfRankThreshold' })),
    preferencesColumnHelper.accessor('tlhValueLow', moneyCol({ id: 'tlhValueLow' })),
    preferencesColumnHelper.accessor('factorWeakNonLoserValue', moneyCol({ id: 'factorWeakNonLoserValue' })),
    preferencesColumnHelper.accessor('factorCombination', textCol({ id: 'factorCombination' })),
];

const tradeInputsColumnHelper = createColumnHelper<DirectStocksSellTradePlanTradeInput>();
const tradeInputsColumns = [
    tradeInputsColumnHelper.accessor(
        'tradeInputRank',
        numberCol({ id: 'tradeInputRank', format: { fractionDigits: 0 } }),
    ),
    tradeInputsColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    tradeInputsColumnHelper.accessor('scId', textCol({ id: 'scId' })),
    tradeInputsColumnHelper.accessor('symbol', textCol({ id: 'symbol' })),
    tradeInputsColumnHelper.accessor('reason', textCol({ id: 'reason' })),
    tradeInputsColumnHelper.accessor('activeRatio', numberCol({ id: 'activeRatio' })),
    tradeInputsColumnHelper.accessor('price', moneyCol({ id: 'price' })),
    tradeInputsColumnHelper.accessor('stockPreferenceCategory', textCol({ id: 'stockPreferenceCategory' })),
    tradeInputsColumnHelper.accessor('currentQuantity', numberCol({ id: 'currentQuantity' })),
    tradeInputsColumnHelper.accessor('minSellAllowance', moneyCol({ id: 'minSellAllowance' })),
    tradeInputsColumnHelper.accessor('maxSellAllowance', moneyCol({ id: 'maxSellAllowance' })),
    tradeInputsColumnHelper.accessor('selectedAllowance', moneyCol({ id: 'selectedAllowance' })),
    tradeInputsColumnHelper.accessor('activeQuantity', numberCol({ id: 'activeQuantity' })),
    tradeInputsColumnHelper.accessor('cumSumDesiredTradeValue', moneyCol({ id: 'cumSumDesiredTradeValue' })),
    tradeInputsColumnHelper.accessor('availableAllowance', moneyCol({ id: 'availableAllowance' })),
    tradeInputsColumnHelper.accessor('tradeValue', moneyCol({ id: 'tradeValue' })),
    tradeInputsColumnHelper.accessor('action', textCol({ id: 'action' })),
    tradeInputsColumnHelper.accessor('tradeQuantity', numberCol({ id: 'tradeQuantity' })),
    tradeInputsColumnHelper.accessor('bidPrice', moneyCol({ id: 'bidPrice' })),
    tradeInputsColumnHelper.accessor('askPrice', moneyCol({ id: 'askPrice' })),
    tradeInputsColumnHelper.accessor('quotePrice', moneyCol({ id: 'quotePrice' })),
    tradeInputsColumnHelper.accessor('priceSource', textCol({ id: 'priceSource' })),
    tradeInputsColumnHelper.accessor('priceTimestamp', dateTimeCol({ id: 'priceTimestamp' })),
];
